import React, { useEffect, useMemo, useRef, useState } from 'react';
import './FAQSchema.css';

// A page-level registry so that multiple <FAQSchema> instances on a single
// page emit one consolidated FAQPage JSON-LD block instead of one per
// instance. This matters on long FAQ pages that use <FAQSchema> to wrap
// several Q&A sections.
const getRegistry = () => {
  if (typeof window === 'undefined') return null;
  if (!window.__ivtFaqRegistry) {
    window.__ivtFaqRegistry = {
      items: [],
      listener: null,
      flush() {
        if (!this.listener) return;
        const cb = this.listener;
        this.listener = null;
        cb([...this.items]);
      }
    };
  }
  return window.__ivtFaqRegistry;
};

const installScript = (items) => {
  // Idempotent: if a FAQPage block is already in the document (for example,
  // captured during prerendering), do not add a second one.
  if (document.querySelector('script[data-ivt-faq-page="true"]')) {
    return;
  }
  if (!items.length) return;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer }
    }))
  };
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-ivt-faq-page', 'true');
  script.text = JSON.stringify(jsonLd);
  document.head.appendChild(script);
};

const FAQSchema = ({ children }) => {
  const wrapperRef = useRef(null);
  const [localItems, setLocalItems] = useState([]);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const qas = [];
    node.querySelectorAll('h3, h4').forEach((heading) => {
      const question = heading.textContent.trim();
      const answers = [];
      let sibling = heading.nextElementSibling;
      while (sibling && !/^H[3-4]$/.test(sibling.tagName)) {
        answers.push(sibling.textContent.trim());
        sibling = sibling.nextElementSibling;
      }
      const answer = answers.filter(Boolean).join(' ').trim();
      if (question && answer) qas.push({ question, answer });
    });
    setLocalItems(qas);
  }, [children]);

  useEffect(() => {
    const registry = getRegistry();
    if (!registry) return;
    if (!localItems.length) return;
    registry.items.push(...localItems);
    // Defer to next tick so all sibling FAQSchema instances have a chance
    // to push their items before we flush.
    if (!registry.listener) {
      registry.listener = (items) => installScript(items);
      setTimeout(() => registry.flush(), 0);
    }
    return () => {
      // Remove our items if we are the only owner; otherwise leave them so
      // a later instance can still flush. This is best-effort cleanup.
      if (registry) {
        registry.items = registry.items.filter(
          (i) => !localItems.some((l) => l.question === i.question)
        );
      }
    };
  }, [localItems]);

  const placeholder = useMemo(() => null, []);
  return (
    <div className="faq-schema" ref={wrapperRef}>
      {children}
      {placeholder}
    </div>
  );
};

export default FAQSchema;
